<template>
    <b-overlay
        variant="white"
        :show="showLoading || busy"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <b-card
            no-body
            :border-variant="borderVariant"
            :class="cardClass"
        >
            <slot name="header">
                <div class="card-header">
                    <b-col
                        md="6"
                        class="pl-0"
                    >
                        <b-card-title>
                            <slot name="header-label" />
                        </b-card-title>
                    </b-col>
                    <b-col
                        md="6"
                        class="pr-0"
                    >
                        <div class="float-right">
                            <slot name="header-controls" />
                        </div>
                    </b-col>
                </div>
            </slot>
            <Transition
                v-if="search"
                mode="out-in"
                name="fade"
            >
                <div
                    :class="tableContainerClass"
                    :style="tableContainerStyle"
                >
                    <table class="table b-table table-hover table-striped bordered">
                        <thead>
                            <tr>
                                <th
                                    v-for="column in columns"
                                    :key="column.key"
                                    :class="column.thClass || ''"
                                >
                                    <slot
                                        :name="'header-cell-' + column.key"
                                        :column="column"
                                    >
                                        <div
                                            :class="getHeaderClass(column)"
                                            :title="column.title"
                                            @click="toggleSort(column)"
                                        >
                                            <span>{{ column.label }}</span>
                                            <feather-icon
                                                v-if="isColumnSortable(column)"
                                                :icon="getSortIcon(column)"
                                                size="14"
                                                class="hamlet-table-sort-icon"
                                            />
                                        </div>
                                    </slot>
                                </th>
                            </tr>
                        </thead>
                        <tbody v-if="search">
                            <tr>
                                <td
                                    v-for="column in columns"
                                    :key="column.key"
                                    style="white-space: nowrap;"
                                >
                                    <slot
                                        :name="'filter-cell-' + column.key"
                                        :column="column"
                                    />
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-if="items.length">
                            <tr
                                v-for="item in items"
                                :key="item.id || item._id || JSON.stringify(item)"
                                @click="onRowClick(item)"
                            >
                                <td
                                    v-for="column in columns"
                                    :key="column.key"
                                    :class="getColumnClass(column, item)"
                                >
                                    <slot
                                        :name="'cell-' + column.key"
                                        :item="item"
                                        :column="column"
                                    >
                                        {{ renderValue(item, column) }}
                                    </slot>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr class="bg-white">
                                <td :colspan="columns.length">
                                    <b-alert
                                        class="m-2"
                                        variant="secondary"
                                        show
                                    >
                                        <div class="alert-body">
                                            <slot name="no-records">
                                                {{ $t('Component(Text):nothing-found') }}
                                            </slot>
                                        </div>
                                    </b-alert>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </Transition>
            <Transition
                v-else
                mode="out-in"
                name="fade"
            >
                <div
                    v-if="items.length"
                    :class="tableContainerClass"
                    :style="tableContainerStyle"
                >
                    <table class="table b-table table-hover table-striped bordered">
                        <thead>
                            <tr>
                                <th
                                    v-for="column in columns"
                                    :key="column.key"
                                    :class="column.thClass || ''"
                                >
                                    <slot
                                        :name="'header-cell-' + column.key"
                                        :column="column"
                                    >
                                        <div
                                            :class="getHeaderClass(column)"
                                            :title="column.title"
                                            @click="toggleSort(column)"
                                        >
                                            <span>{{ column.label }}</span>
                                            <feather-icon
                                                v-if="isColumnSortable(column)"
                                                :icon="getSortIcon(column)"
                                                size="14"
                                                class="hamlet-table-sort-icon"
                                            />
                                        </div>
                                    </slot>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="item in items"
                                :key="item.id || item._id || JSON.stringify(item)"
                                @click="onRowClick(item)"
                            >
                                <td
                                    v-for="column in columns"
                                    :key="column.key"
                                    :class="getColumnClass(column, item)"
                                >
                                    <slot
                                        :name="'cell-' + column.key"
                                        :item="item"
                                        :column="column"
                                    >
                                        {{ renderValue(item, column) }}
                                    </slot>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <b-alert
                    v-else-if="showEmptyMessage"
                    class="m-2"
                    variant="secondary"
                    show
                >
                    <div class="alert-body">
                        <slot name="no-records">
                            {{ $t('Component(Text):nothing-found') }}
                        </slot>
                    </div>
                </b-alert>
                <div v-else />
            </Transition>
            <div
                v-if="showPaging && paginationReady"
                class="pl-2 pr-2 pb-2 d-flex justify-content-between flex-wrap"
            >
                <div class="d-flex align-items-center justify-content-center">
                    <div>
                        {{ $t('Component(Grid):records-found', {total: totalItems}) }}
                    </div>
                    <div class="pl-1 pr-1">
                        <b-form-select
                            id="perPageSelect"
                            v-model="perPage"
                            inline
                            :options="pageOptions"
                            @input="onPerPageChange"
                        />
                    </div>
                    <div>{{ $t('Component(Grid):records-per-page') }}</div>
                </div>

                <div>
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="totalItems"
                        :per-page="perPage"
                        first-number
                        last-number
                        prev-class="prev-item"
                        next-class="next-item"
                        class="mb-0"
                        @input="onPageChange"
                    >
                        <template #prev-text>
                            <feather-icon
                                icon="ChevronLeftIcon"
                                size="18"
                            />
                        </template>
                        <template #next-text>
                            <feather-icon
                                icon="ChevronRightIcon"
                                size="18"
                            />
                        </template>
                    </b-pagination>
                </div>
            </div>
        </b-card>
    </b-overlay>
</template>

<script>
import {
    BCard, BCol, BOverlay, BCardTitle, BPagination, BFormSelect, BAlert,
} from 'bootstrap-vue'

function normalizeSort(sort) {
    if (!sort || typeof sort !== 'object' || Array.isArray(sort)) {
        return null
    }

    if (!sort.by || !['asc', 'desc'].includes(sort.direction)) {
        return null
    }

    return {
        by: sort.by,
        direction: sort.direction,
    }
}

function resolveInitialSort(columns, defaultSort) {
    const normalizedDefaultSort = normalizeSort(defaultSort)
    if (normalizedDefaultSort) {
        return normalizedDefaultSort
    }

    const defaultSortColumn = columns.find(column => ['asc', 'desc'].includes(column.defaultSort))
    if (!defaultSortColumn) {
        return null
    }

    return {
        by: defaultSortColumn.sortKey || defaultSortColumn.key,
        direction: defaultSortColumn.defaultSort,
    }
}

function cloneObject(value) {
    return JSON.parse(JSON.stringify(value))
}

function omitNilSort(config) {
    if (!config.sort) {
        const nextConfig = { ...config }
        delete nextConfig.sort
        return nextConfig
    }

    return config
}

export default {
    components: {
        BCard,
        BCol,
        BFormSelect,
        BOverlay,
        BAlert,
        BCardTitle,
        BPagination,
    },
    props: {
        borderVariant: {
            default: '',
        },
        cardClass: {
            default: '',
        },
        tdClassProvider: {
            default: null,
        },
        tableContainerClass: {
            default: 'table-responsive hamlet-table',
        },
        tableContainerStyle: {
            default: '',
        },
        search: {
            default: false,
        },
        busy: {
            type: Boolean,
            default: false,
        },
        columns: {
            type: Array,
            default() {
                return []
            },
        },
        fetch: {
            default() {
                return () => {
                    this.showLoading = false
                }
            },
        },
        pageOptions: {
            type: Array,
            default() {
                return [5, 10, 15, 30]
            },
        },
        defaultPerPage: {
            type: Number,
            default: 10,
        },
        defaultPage: {
            type: Number,
            default: 1,
        },
        showPagingWhenEmpty: {
            default: true,
        },
        showEmptyMessage: {
            default: true,
        },
        paging: {
            type: Boolean,
            default: true,
        },
        defaultFilters: {
            type: Object,
            default() {
                return {}
            },
        },
        defaultSort: {
            type: Object,
            default() {
                return null
            },
        },
        sorting: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentPage: this.defaultPage,
            perPage: this.defaultPerPage,
            totalItems: 0,
            items: [],
            paginationReady: false,
            showLoading: false,
            filters: cloneObject(this.defaultFilters),
            sort: resolveInitialSort(this.columns, this.defaultSort),
        }
    },
    computed: {
        showPaging() {
            if (!this.paging) {
                return false
            }
            if (this.items.length > 0) {
                return true
            }

            return this.showPagingWhenEmpty
        },
    },
    created() {
        this.$emit('created', this.getConfig(), this.fetchHandler, this.showLoader)
        this.makeRequest()
    },
    methods: {
        isColumnSortable(column) {
            return this.sorting && column.sortable === true
        },
        getColumnSortKey(column) {
            return column.sortKey || column.key
        },
        isActiveSortColumn(column) {
            return this.sort && this.sort.by === this.getColumnSortKey(column)
        },
        getSortIcon(column) {
            if (!this.isActiveSortColumn(column)) {
                return 'ChevronsUpIcon'
            }

            return this.sort.direction === 'asc'
                ? 'ChevronUpIcon'
                : 'ChevronDownIcon'
        },
        getHeaderClass(column) {
            return [
                column.class || '',
                'hamlet-table-header-label',
                {
                    'hamlet-table-header-sortable': this.isColumnSortable(column),
                    'hamlet-table-header-active-sort': this.isActiveSortColumn(column),
                },
            ]
        },
        renderValue(item, column) {
            if (Object.prototype.hasOwnProperty.call(item, column.key)) {
                if (item[column.key] instanceof Object && Object.prototype.hasOwnProperty.call(item[column.key], 'title')) {
                    return item[column.key].title
                }

                return item[column.key]
            }

            return ''
        },
        getColumnClass(column, item) {
            if (this.tdClassProvider) {
                return (this.tdClassProvider)(column, item)
            }
            return column.tdClass
        },
        onPerPageChange() {
            this.currentPage = 1
            this.$emit('per-page-change', this.perPage)
            this.$emit('page-change', this.currentPage)
            this.makeRequest()
        },
        onPageChange() {
            this.$emit('page-change', this.currentPage)
            this.makeRequest()
        },
        toggleSort(column) {
            if (!this.isColumnSortable(column)) {
                return
            }

            const sortBy = this.getColumnSortKey(column)

            if (!this.sort || this.sort.by !== sortBy) {
                this.sort = {
                    by: sortBy,
                    direction: 'asc',
                }
            } else if (this.sort.direction === 'asc') {
                this.sort = {
                    by: sortBy,
                    direction: 'desc',
                }
            } else {
                this.sort = null
            }

            this.currentPage = 1
            this.$emit('page-change', this.currentPage)
            this.makeRequest()
        },
        makeRequest() {
            this.showLoading = true
            this.fetch(this.getConfig(), this.fetchHandler)
        },
        refresh() {
            this.makeRequest()
        },
        showLoader() {
            this.showLoading = true
        },
        onRowClick(item) {
            this.$emit('onRowClick', item)
        },
        filter(key, value) {
            if (value === null || undefined === value || value === '') {
                if (Object.prototype.hasOwnProperty.call(this.filters, key)) {
                    delete this.filters[key]
                }
            } else {
                this.filters[key] = value
            }
            this.currentPage = 1
            this.$emit('page-change', this.currentPage)
            this.makeRequest()
        },
        unsetFilter(key) {
            delete this.filters[key]
            this.currentPage = 1
            this.$emit('page-change', this.currentPage)
            this.makeRequest()
        },
        getConfig() {
            return omitNilSort({
                filters: this.filters,
                page: this.currentPage,
                perPage: this.perPage,
                sort: this.sort,
            })
        },
        fetchHandler(totalItems, items) {
            this.totalItems = totalItems
            this.items = items
            this.paginationReady = true
            this.showLoading = false
        },
    },
}
</script>

<style lang="scss">
.hamlet-table-header-label {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.hamlet-table-header-sortable {
    cursor: pointer;
    user-select: none;
}

.hamlet-table-sort-icon {
    flex-shrink: 0;
}
</style>
